import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { copyToClipboard } from '../../../models/utils/uiUtils';
import { useAIConversationChatMessageQuery } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useCoachingConversationCallView } from '../../../queries/AIConversation/useCoachingConversationCallView';
import { Button } from '../../../web/components-v2/Button';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../web/components-v2/Collapsible';
import { Pill } from '../../../web/components-v2/Pill';
import { Avatar } from '@HoudiniUI/Avatar';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { ClipboardIcon } from '../../../web/components/svgs/icons/ClipboardIcon';
import { ThumbsDownIcon } from '../../../web/components/svgs/icons/ThumbsDownIcon';
import { ThumbsUpIcon } from '../../../web/components/svgs/icons/ThumbsUpIcon';
import { formatAgentTemplate, formatUiVersion } from './callDisplay';
import { getCreatorInitials, getCreatorLabel } from './creatorLabel';
import { getLeadLabel } from './leadLabel';
import { styleSheet } from './styles';

interface IProps {
	conversationId: string | null;
}

/** Resets the "Copied" affordance back to "Copy" this long after a successful copy. */
const COPY_RESET_MS = 1200;

/**
 * Humanizes a PascalCase conversation type for display, e.g. `SalesLiveCoaching` -> `Sales Live
 * Coaching`. Inserts a space at lower→upper boundaries and between an acronym run and a following
 * word, so the pill reads as words rather than one run-on token.
 */
const formatConversationType = (value: string): string =>
	value
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim();

/**
 * Formats an ISO date string with the same `date-fns` pattern `ConversationList` uses, so
 * timestamps read identically across the page. Falls back to an em dash for missing/unparseable
 * values.
 */
const formatTimestamp = (value?: string): string => {
	if (!value) {
		return '—';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '—';
	}

	return format(date, "MMM d, yyyy 'at' h:mm a");
};

/**
 * Read-only metadata summary for the currently selected coaching conversation. Sits above the
 * insights + transcript on the Coffee AI coaching review detail pane.
 *
 * The hooks here re-read queries that are already populated elsewhere on the page
 * (`useSalesLiveCoachingConversationsQuery` by `ConversationList`, `useAIConversationChatMessageQuery`
 * by `TranscriptPanel`). react-query dedupes by query key, so calling them again resolves from cache
 * and issues no additional network request. The linked phone call (incl. its `uiVersion`) now arrives
 * joined onto each conversation by `aida/coffee/coaching/filter`, so we read it from that same list
 * query rather than making a separate per-conversation request.
 *
 * The coaching adherence breakdown is rendered by `AdherenceDrawerContent` in the right-side
 * drawer; this panel stays metadata-only.
 */
export function MetadataPanel({ conversationId }: IProps) {
	const [isOpen, setIsOpen] = React.useState(true);
	const [copied, setCopied] = React.useState(false);

	// Resolve the selected conversation's call view — from the loaded list when present, else fetched
	// by id so a deep link (`?convo=<id>`) to a conversation past the loaded pages still populates.
	const { callView } = useCoachingConversationCallView(conversationId);
	const { data: messages } = useAIConversationChatMessageQuery({
		id: conversationId ?? '',
		enabled: Boolean(conversationId),
	});

	const conversation = callView?.conversation;

	const feedbackTally = React.useMemo(() => {
		const tally = { up: 0, down: 0 };
		for (const message of messages ?? []) {
			if (message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp) {
				tally.up += 1;
			} else if (message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown) {
				tally.down += 1;
			}
		}
		return tally;
	}, [messages]);

	// The model isn't tracked on the conversation or the phone call — it's stamped per assistant
	// message, so a conversation can legitimately span more than one. Summarize the distinct set.
	const modelSummary = React.useMemo(() => {
		const labels = new Set<string>();
		for (const message of messages ?? []) {
			const label = [message.provider, message.model].filter(Boolean).join(' · ');
			if (label) {
				labels.add(label);
			}
		}
		const all = [...labels];
		if (all.length === 0) {
			return { value: '—', title: undefined as string | undefined };
		}
		return {
			value: all.length === 1 ? all[0] : `${all[0]} +${all.length - 1}`,
			title: all.join('\n'),
		};
	}, [messages]);

	// The linked phone call (with its backend-stamped `uiVersion`) is joined onto the call view — null
	// when the conversation has no linked call.
	const phoneCall = callView?.phoneCall ?? null;
	const companyName = callView?.company?.companyName?.trim() ?? '';
	const leadLabel = getLeadLabel(callView?.contacts);

	// Reset the copied affordance after a short delay; cleared on unmount or re-copy.
	React.useEffect(() => {
		if (!copied) {
			return undefined;
		}
		const timeout = setTimeout(() => setCopied(false), COPY_RESET_MS);
		return () => clearTimeout(timeout);
	}, [copied]);

	// Nothing selected: render nothing. The transcript panel already shows the "select a
	// conversation" prompt, so a second placeholder here would be redundant noise.
	if (!conversationId || !conversation) {
		return null;
	}

	const handleCopyId = async () => {
		const didCopy = await copyToClipboard(conversation.id);
		if (didCopy) {
			setCopied(true);
		}
	};

	const messageCount = messages?.length ?? 0;
	const repLabel = getCreatorLabel(conversation.creator);
	// Read the conversation's copy, not the call's: a conversation with no linked call still ran on a
	// template, and both halves carry the same pair.
	const agentTemplate = formatAgentTemplate(conversation);

	const stats: Array<{ label: string; value: React.ReactNode; title?: string }> = [
		{ label: 'Company', value: companyName || '—' },
		{ label: 'Lead', value: leadLabel || '—' },
		{ label: 'Created', value: formatTimestamp(conversation.creationDate) },
		{ label: 'Last modified', value: formatTimestamp(conversation.lastModifiedDate) },
		{ label: 'Participants', value: conversation.participants?.length ?? 0 },
		{ label: 'Messages', value: messageCount },
		{ label: 'UI version', value: formatUiVersion(phoneCall?.uiVersion) },
		{ label: 'Agent', value: agentTemplate?.label ?? '—', title: agentTemplate?.title },
		{ label: 'Model', value: modelSummary.value, title: modelSummary.title },
	];

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen} className={css(styleSheet.metadata)}>
			<div className={css(styleSheet.metadataHeader)}>
				<h3 className={css(styleSheet.metadataTitle)}>Conversation details</h3>
				<CollapsibleTrigger render={<Button variant='ghost' size='icon' aria-label={isOpen ? 'Collapse' : 'Expand'} />}>
					<ChevronDown
						className={css(styleSheet.collapseChevron, isOpen ? styleSheet.collapseChevronOpen : null)}
						width={18}
						height={18}
					/>
				</CollapsibleTrigger>
			</div>
			<CollapsiblePanel>
				<div className={css(styleSheet.metadataBody)}>
					<div className={css(styleSheet.metaTop)}>
						<Avatar
							fallbackText={getCreatorInitials(conversation.creator)}
							altText={repLabel}
							smallText
							styles={[styleSheet.metaAvatar]}
						/>
						<div className={css(styleSheet.metaIdentity)}>
							<span className={css(styleSheet.metaRep)}>{repLabel}</span>
							{conversation.conversationType ? (
								<Pill
									variant='tag'
									color='teal'
									size='small'
									label={formatConversationType(conversation.conversationType)}
								/>
							) : null}
						</div>
						<div className={css(styleSheet.metaFeedback)}>
							<span
								className={css(styleSheet.fbChip, styleSheet.fbChipUp)}
								aria-label={`${feedbackTally.up} thumbs up`}
								title={`${feedbackTally.up} thumbs up`}
							>
								<ThumbsUpIcon width={13} height={13} aria-hidden='true' />
								{feedbackTally.up}
							</span>
							<span
								className={css(styleSheet.fbChip, styleSheet.fbChipDown)}
								aria-label={`${feedbackTally.down} thumbs down`}
								title={`${feedbackTally.down} thumbs down`}
							>
								<ThumbsDownIcon width={13} height={13} aria-hidden='true' />
								{feedbackTally.down}
							</span>
						</div>
					</div>

					<dl className={css(styleSheet.metaStats)}>
						{stats.map(stat => (
							<div className={css(styleSheet.metaStat)} key={stat.label} title={stat.title}>
								<dt className={css(styleSheet.metaStatLabel)}>{stat.label}</dt>
								<dd className={css(styleSheet.metaStatValue)}>{stat.value}</dd>
							</div>
						))}
					</dl>

					<div className={css(styleSheet.metaIdRow)}>
						<span className={css(styleSheet.metaStatLabel)}>Conversation ID</span>
						<span className={css(styleSheet.metaIdValue)}>{conversation.id}</span>
						<Button variant='ghost' size='sm' onClick={handleCopyId} styles={[styleSheet.metaIdCopy]}>
							<ClipboardIcon className={css(styleSheet.metaIdCopyIcon)} fillColor='currentColor' />
							{copied ? 'Copied' : 'Copy'}
						</Button>
					</div>
				</div>
			</CollapsiblePanel>
		</Collapsible>
	);
}
